import { LineChart, StackedAreaChart } from "@get-dx/d3-charts";
import {
  getQueryStringParams,
  setQueryStringParam,
  deleteQueryStringParam,
  objectToQueryString,
  generatePreviousComparisonValues,
  generateBenchmarkComparisonValues,
  numberToSpelling,
  snapshotSquadsFromCache,
  generateOrgComparisonValues,
} from "../../shared/utils";
import { chartTooltipHtml } from "../chartTooltipHtml";
import { teamTreeExpansionMixin } from "../mixins/teamTreeExpansion";
import { peopleFiltersMixin } from "../mixins/peopleFilters";
import { tooltipIfTruncated } from "../../lib/utils";
import { getDistributionColors } from "../chartColors";

const trendOptionsMap = new Map([
  ["vs_prev", "prev_percentage"],
  ["vs_org", "org_percentage"],
  ["vs_50th", "benchmark_50"],
  ["vs_75th", "benchmark_75"],
  ["vs_90th", "benchmark_90"],
]);
function generateComparisonValues(
  resource,
  benchmark,
  resourceType = "factor",
) {
  let comparisonValues = [];

  if (
    ["vs_prev", "prev_percentage"].includes(benchmark) &&
    resource.values.length > 1
  ) {
    comparisonValues = generatePreviousComparisonValues(resource);
  } else if (!["vs_prev", "prev_percentage"].includes(benchmark)) {
    comparisonValues = generateBenchmarkComparisonValues(
      resource,
      resourceType,
      benchmark,
    );
  }

  return comparisonValues;
}

export function snapshotDelivery(defaults) {
  const {
    baseUrl,
    drilldownTabs,
    minResponseThreshold,
    tagOptions,
    groups,
    snapshotId,
  } = defaults;

  const drilldownTab = getQueryStringParams().get("ddt");
  // Ensure valid drilldown tab is set
  if (drilldownTab && !drilldownTabs.some((t) => t.tab == drilldownTab)) {
    setQueryStringParam("ddt", "overview");
  }

  return {
    ...teamTreeExpansionMixin(),
    ...peopleFiltersMixin(),

    // Configure ID mapping
    rowIdKey: "snapshot_squad_id",

    kpiScores: null,
    drilldownTabs,
    groups,
    drilldownData: {},
    drilldownModalOpen: false,
    selectedKpi: null,
    selectedDrilldownTab: getQueryStringParams().get("ddt"),
    tabDataLoaded: false,
    tableDataLoaded: false,
    isFetchingTableData: false,
    trends: null,
    xStart: null,
    maxKpiScore: null,
    xEnd: null,
    responseCount: null,
    squadNamesFromSquadIds: [],
    showTeamBreakdown: true,
    kpiScoresLoaded: false,
    tooltipIfTruncated,
    init() {
      this.$root.addEventListener("dx:squadsTree:init", () => {
        this.determineSquadNamesFromSquadIds();
        // Loading directly to modal from URL cached squads are empty, so wait for event
        if (this.squadIds.length != 1) {
          this.showTeamBreakdown = true;
        } else {
          const matchedSquad = this.cachedSquads.find(
            (squad) => squad.id == this.squadIds[0],
          );
          this.showTeamBreakdown = matchedSquad?.is_parent;
        }
      });

      this.fetchAll().then(() => {
        if (this.selectedDrilldownTab) {
          const selectedItem = drilldownTabs.find(
            (t) => t.tab == this.selectedDrilldownTab,
          );
          this.fetchTab(this.selectedDrilldownTab, selectedItem.url);
        }
      });

      this.$watch("$store.snapshotResultFilters.compareTo", () => {
        this.handleComparisonChange();
      });
      this.$watch("$store.snapshotResultFilters.previousSnapshotId", () => {
        this.handleComparisonChange();
      });
    },
    get cachedSquads() {
      return snapshotSquadsFromCache(snapshotId);
    },

    /**
     * Required by teamTreeExpansionMixin - provides the API endpoint for fetching child team data
     * when expanding team rows in the breakdown table. Do not remove this method.
     * @returns {string} The URL endpoint for child data requests
     */
    getChildDataEndpoint() {
      const selectedItem = drilldownTabs.find(
        (t) => t.tab == this.selectedDrilldownTab,
      );
      return selectedItem ? selectedItem.url : "";
    },

    /**
     * Required by teamTreeExpansionMixin - provides query parameters for fetching child team data
     * when expanding team rows. Do not remove this method.
     * @returns {Object} Parameters object containing filters and context for the API request
     */
    getChildDataParams() {
      return {
        ct: this.compareTo,
        prev_ss: this.previousSnapshotId,
        squad_ids: this.squadIds.join(","),
        group_ids: this.groupIds.join(","),
        tag_ids: this.tagIds.join(","),
        factor_id: this.selectedKpi?.id,
        branch: this.$store.snapshotResultFilters.branch,
        aggregation: this.$store.snapshotResultFilters.aggregation,
      };
    },

    /**
     * Required by teamTreeExpansionMixin - provides benchmark data to apply to child team rows
     * when they are loaded. Do not remove this method.
     * @returns {Object} Benchmark data with p_50, p_75, p_90 properties
     */
    getBenchmarkData() {
      // Delivery has benchmark data from the benchmark_factor
      const benchmarkFactor =
        this.drilldownData?.team_breakdown?.data?.benchmark_factor;
      return benchmarkFactor || {};
    },

    /**
     * Required by teamTreeExpansionMixin - provides cached squad data for parent/child lookups
     * and team hierarchy navigation. Do not remove this method.
     * @returns {Array} Array of squad objects with id, parent_id, is_parent properties
     */
    getCachedSquads() {
      return this.cachedSquads || [];
    },
    get filtersApplied() {
      return (
        this.$store.snapshotResultFilters.tagIds.length ||
        this.$store.snapshotResultFilters.squadIds.length
      );
    },
    determineSquadNamesFromSquadIds() {
      const selectedSquads = this.cachedSquads.filter(
        (squad) =>
          this.squadIds.includes(String(squad.id)) ||
          this.squadIds.includes(squad.id),
      );

      this.squadNamesFromSquadIds = selectedSquads.map((squad) => squad.name);
      // Loading directly to modal from URL cached squads are empty, so wait for event
      if (this.squadIds.length != 1) {
        this.showTeamBreakdown = true;
      } else {
        this.showTeamBreakdown = this.cachedSquads.find(
          (squad) => squad.id == this.squadIds[0],
        )?.is_parent;
      }
    },
    get showOrgComparison() {
      return this.$store.snapshotResultFilters.squadIds.length > 0;
    },
    get kpiItems() {
      const items = this.kpiScores?.filter((i) => i.current_snapshot);

      return items;
    },
    get compareTo() {
      return this.$store.snapshotResultFilters.compareTo;
    },
    get previousSnapshotId() {
      return this.$store.snapshotResultFilters.previousSnapshotId;
    },
    get selectedKpiComparison() {
      const rowData = this.selectedKpi;

      if (!rowData) return null;

      return rowData[this.compareTo];
    },
    get tagIds() {
      return this.$store.snapshotResultFilters.tagIds;
    },
    get squadIds() {
      const storeValue = this.$store?.snapshotResultFilters?.squadIds;
      const urlValue =
        getQueryStringParams()
          .get("squad_ids")
          ?.split(",")
          .filter(Boolean)
          .map(Number) || [];
      return storeValue && storeValue.length > 0 ? [...storeValue] : urlValue;
    },
    get groupIds() {
      const storeValue = this.$store?.snapshotResultFilters?.groupIds;
      const urlValue =
        getQueryStringParams()
          .get("group_ids")
          ?.split(",")
          .filter(Boolean)
          .map(Number) || [];
      return storeValue && storeValue.length > 0 ? [...storeValue] : urlValue;
    },
    get drilldownSortBy() {
      return this.$store.snapshotResultFilters.drilldownSortBy;
    },
    get drilldownSortReverse() {
      return this.$store.snapshotResultFilters.drilldownSortReverse;
    },
    get hasMultipleTrendValues() {
      const validValues = this?.trends?.values?.filter(
        (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
      );
      return validValues?.length >= 2;
    },
    get visibleDrilldownTabs() {
      let filteredTabs = drilldownTabs.filter((drilldownTab) => {
        if (drilldownTab.tab == "team_breakdown") {
          return this.showTeamBreakdown && !this.isFilteringByGroups();
        }
        return true;
      });

      return filteredTabs;
    },
    get tagNamesFromTagIds() {
      const selectedTags = tagOptions.filter(
        (tag) =>
          this.tagIds.includes(String(tag.id)) || this.tagIds.includes(tag.id),
      );

      return selectedTags.map((tag) => tag.name);
    },
    kpiDescription(item) {
      if (item.speed) {
        return "Perceived rate of delivery.";
      }
      if (item?.effort_to_deliver) {
        return "Ease of being a technical contributor.";
      }
      if (item?.reliability) {
        return "Perceived technical stability of systems.";
      }
      if (item.time_loss) {
        return "Time lost due to inefficiencies.";
      }
      if (item.engagement) {
        return "Enthusiasm towards work.";
      }
      if (item.name.toUpperCase() == "Perceived Productivity".toUpperCase()) {
        return "Self-reported productivity.";
      }
    },
    openDrilldownModal(item) {
      // Don't show any modals if not enough responses
      if (item?.count < minResponseThreshold) return;

      this.drilldownModalOpen = true;
      this.selectedKpi = item;
      this.selectedDrilldownTab = "overview";

      setQueryStringParam("factor_id", item.id);
      setQueryStringParam("ddt", "overview");
      this.fetchTab(this.drilldownTabs[0].tab, this.drilldownTabs[0].url);
    },
    hideDeliveryDetails() {
      this.drilldownModalOpen = false;
      this.selectedKpi = null;
      this.selectedDrilldownTab = null;
      // Clear the data so we don't run into weird cache issues.
      this.drilldownData = {};
      this.trends = null;

      deleteQueryStringParam("ddt");
      deleteQueryStringParam("factor_id");
    },
    showDistributionLabel(item, position) {
      // Guard against race condition where item might be null
      // when component renders before data is loaded
      if (!item) return "";
      let label;

      let options;
      try {
        options = item.options ? JSON.parse(item.options) : null;
      } catch (e) {
        console.error("Failed to parse options:", e);
        return "";
      }
      // Guard against race condition where options might not be loaded yet
      if (!options) return "";

      if (item?.reliability || item?.speed) {
        label = this.showOptionLabel(options[position - 1]);
      } else if (item?.time_loss) {
        label = this.showOptionLabel(options[position]);
      } else {
        switch (position) {
          case 1:
            label = this.showOptionLabel(options[4]);
            break;
          case 2:
            label = this.showOptionLabel(options[3]);
            break;
          case 3:
            label = this.showOptionLabel(options[2]);
            break;
          case 4:
            label = this.showOptionLabel(options[1]);
            break;
          case 5:
            label = this.showOptionLabel(options[0]);
            break;
        }
      }

      // Provide fallback value if label is undefined during initial render
      return label || "";
    },
    showOptionLabel(optionLabel) {
      if (typeof optionLabel == "object") return optionLabel[0];

      return optionLabel;
    },
    calculateItemComparison(item) {
      if (this.compareTo == "vs_50th") {
        return item.score - item.benchmark_50;
      } else if (this.compareTo == "vs_75th") {
        return item.score - item.benchmark_75;
      } else if (this.compareTo == "vs_90th") {
        return item.score - item.benchmark_90;
      } else if (this.compareTo == "vs_prev") {
        return item.previous_score != null
          ? item.score - item.previous_score
          : null;
      } else if (this.compareTo == "vs_org") {
        return item.org_score != null
          ? item.score - item.org_score
          : item.score - this.selectedKpi?.org_score;
      }
    },
    percentageOfResponsesOverview(positionCount, totalFieldCount) {
      const item = this.selectedKpi;

      if (!item) return;

      if (item[totalFieldCount] === 0) return "0%";

      return `${Math.round(
        (100 * item[positionCount]) / item[totalFieldCount],
      )}%`;
    },
    kpiDistributionItems() {
      const item = this.selectedKpi;
      if (!item) return [];

      // Parse options from JSON
      let options;
      try {
        options = item.options ? JSON.parse(item.options) : null;
      } catch (e) {
        console.error("Failed to parse KPI options:", e);
        return [];
      }
      if (!options) return [];

      // Determine count fields based on time_loss flag
      const countFields = item.time_loss
        ? [
            "zero_count",
            "one_count",
            "two_count",
            "three_count",
            "four_count",
            "five_count",
          ]
        : ["one_count", "two_count", "three_count", "four_count", "five_count"];

      // Map count fields to distribution items
      return countFields.map((field, index) => {
        const count = item[field] || 0;
        const percentage =
          item.count > 0 ? Math.round((100 * count) / item.count) : 0;

        // Get label based on KPI type
        let label;
        if (item.reliability || item.speed) {
          // Direct mapping: index 0→option[0], index 1→option[1], etc.
          label = this.showOptionLabel(options[index]);
        } else if (item.time_loss) {
          // Direct mapping for time_loss: index 0→option[0], index 1→option[1], etc.
          label = this.showOptionLabel(options[index]);
        } else {
          // Reverse mapping for others: index 0→option[4], index 1→option[3], etc.
          const reverseIndex = countFields.length - 1 - index;
          label = this.showOptionLabel(options[reverseIndex]);
        }

        return {
          label: label || "",
          count: count,
          percentage: `${percentage}%`,
        };
      });
    },
    fetchTab(tab, url) {
      if (!this.drilldownData[tab]) {
        this.tabDataLoaded = false;
      }

      if (tab === "team_breakdown") {
        this.resetTreeState();
        this.isFetchingTableData = true;

        // Set default drilldown sort if not specified in URL
        if (!getQueryStringParams().get("ddsb")) {
          this.$store.snapshotResultFilters.drilldownSortBy =
            "snapshotSquadName";
          this.$store.snapshotResultFilters.drilldownSortReverse = false;
          setQueryStringParam("ddsb", "snapshotSquadName");
          setQueryStringParam("ddsr", "false");
        }
      }

      // Make this smarter so if we've already fetched the tab for the SAME factor we don't fetch it again
      this.selectedDrilldownTab = tab;
      setQueryStringParam("ddt", tab);

      return fetch(`${url}${this.queryParams()}`)
        .then((resp) => resp.json())
        .then((data) => {
          if (tab == "overview") {
            data.trends.forEach((factorData) => {
              const benchmarkFactor =
                data.benchmark_factors[factorData.factor["id"]];

              factorData.factor["benchmark_50"] = benchmarkFactor.p_50;
              factorData.factor["benchmark_75"] = benchmarkFactor.p_75;
              factorData.factor["benchmark_90"] = benchmarkFactor.p_90;
            });
          }

          if (tab == "team_breakdown") {
            const benchmarkFactor = data.benchmark_factor;

            data.result_items.forEach((item) => {
              item["benchmark_50"] = benchmarkFactor["p_50"];
              item["benchmark_75"] = benchmarkFactor["p_75"];
              item["benchmark_90"] = benchmarkFactor["p_90"];
            });
          }

          this.drilldownData[tab] = { data };
          this.tabDataLoaded = true;
          if (tab === "team_breakdown") {
            this.tableDataLoaded = true;
            this.isFetchingTableData = false;
          }
          this.renderTabDetails(tab, data);
        });
    },
    queryParams() {
      const paramObj = {
        ct: this.compareTo,
        prev_ss: this.previousSnapshotId,
        squad_ids: `${this.squadIds.join(",")}`,
        group_ids: `${this.groupIds.join(",")}`,
        tag_ids: `${this.tagIds.join(",")}`,
        factor_id: this.selectedKpi?.id,
        branch: this.$store.snapshotResultFilters.branch,
        aggregation: this.$store.snapshotResultFilters.aggregation,
        parent_squad_id: this.currentParentSquadId,
      };

      return `?${objectToQueryString(paramObj)}`;
    },
    fetchAll() {
      // Bridge method for peopleFiltersMixin
      return this.fetchResultItems();
    },
    fetchResultItems() {
      const paramsObj = {
        ct: this.compareTo,
        prev_ss: this.previousSnapshotId,
        squad_ids: this.squadIds.join(","),
        group_ids: this.groupIds.join(","),
        tag_ids: this.tagIds.join(","),
        branch: this.$store.snapshotResultFilters.branch,
      };

      const queryParams = objectToQueryString(paramsObj);

      return fetch(`${baseUrl}?${queryParams}`)
        .then((resp) => resp.json())
        .then((data) => {
          if (typeof data.benchmark_factors == "object") {
            data.kpi_scores.forEach((row) => {
              const benchmarkFactor = data.benchmark_factors[row["id"]];

              if (
                benchmarkFactor &&
                row["score"] !== null &&
                row["score"] !== undefined
              ) {
                row["benchmark_50"] = benchmarkFactor["p_50"];
                row["benchmark_75"] = benchmarkFactor["p_75"];
                row["benchmark_90"] = benchmarkFactor["p_90"];

                row["vs_50th"] = row["score"] - benchmarkFactor["p_50"];
                row["vs_75th"] = row["score"] - benchmarkFactor["p_75"];
                row["vs_90th"] = row["score"] - benchmarkFactor["p_90"];
              }
            });
          }

          this.kpiScores = data.kpi_scores;
          this.kpiScoresLoaded = true;
          window.dispatchEvent(
            new CustomEvent("kpiResultsFetched", {
              detail: { kpiResults: this.kpiScores },
            }),
          );
          const targetedKpiId = getQueryStringParams().get("factor_id");
          this.selectedKpi = targetedKpiId
            ? this.kpiScores
                ?.filter((i) => i.current_snapshot)
                ?.find((score) => score.id == targetedKpiId)
            : null;

          if (this.selectedKpi) {
            this.drilldownModalOpen = true;
            const newKpi = this.kpiScores
              .reverse()
              .find((obj) => obj.id == this.selectedKpi.id);

            const selectedKpiToSet = {
              ...this.selectedKpi,
              prev_score: newKpi.prev_score,
              previous_score: newKpi.previous_score,
              vs_prev: newKpi.score - newKpi.prev_score,
            };

            this.selectedKpi = selectedKpiToSet;
          }
        });
    },
    renderTabDetails(tab, data) {
      if (tab == "overview") {
        // We get all of the outcome trends in one query so we can reduce queries in the future
        this.trends = data.trends.find(
          (trend) => trend.factor.id == this.selectedKpi?.id,
        );
        // Find first value with non-null data (matching what the chart displays)
        const firstValidValue = this.trends.values.find(
          (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
        );
        this.xStart = firstValidValue ? firstValidValue.date : data.x_start;
        this.renderDriverTrendChart();
        this.renderDriverDistributionChart();
      }
      if (tab == "team_breakdown") {
        this.calculateMaxTeamScore();
      }
    },
    calculateMaxTeamScore() {
      const arr = this.breakdownItems.map((i) => i.score);

      this.maxKpiScore = Math.max(...arr);
    },
    percentageOfResponses(item, positionCount) {
      if (item.count === 0) return "0%";

      return `${Math.round((100 * item[positionCount]) / item.count)}%`;
    },
    xStartLabel() {
      if (!this.xStart) return "";
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(this.xStart));
    },
    renderDriverTrendChart() {
      const elChart = document.getElementById(`kpi-trends-chart`);
      if (!elChart) return;

      elChart.innerHTML = null;
      const benchmark = this.compareTo;
      const minVal = Math.min(
        ...this.trends.values
          .map((v) => v.value)
          .concat(this.trends.factor.benchmark_50),
      );
      const maxVal = Math.max(
        ...this.trends.values
          .map((v) => v.value)
          .concat(this.trends.factor.benchmark_90),
      );

      const trendSize = this.trends.values.length;
      this.xEnd = this.trends.values[trendSize - 1]?.label;
      // Avoid using 'this' in passing in function for the tooltip callback, get the string explicitly
      const heading = this.selectedKpi?.name;
      const timeLoss = this.selectedKpi.time_loss;

      // Filter out null values before passing to chart
      const validValues = this.trends.values.filter(
        (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
      );

      const attrs = {
        elChart: elChart,
        startDate: this.xStart,
        endDate:
          validValues[validValues.length - 1]?.date ||
          this.trends.values[trendSize - 1]?.date,
        tooltipHtml(d, cd) {
          const params = {
            heading: heading,
            d: {
              label: d.label,
              value: d.value,
              formattedValue: d.value,
            },
          };

          if (cd) {
            params.cd = {
              label: cd.label,
              value: cd.value,
              formattedValue: cd.value,
            };
          }

          return timeLoss
            ? chartTooltipHtml.timeLoss(params)
            : chartTooltipHtml.default(params);
        },
        yAxisMin: minVal * 0.8,
        yAxisMax: maxVal * 1.2,
        showXAxisTicks: false,
        values: validValues,
      };

      if (
        benchmark &&
        (this.trends.factor[trendOptionsMap.get(benchmark)] ||
          benchmark === "vs_prev")
      ) {
        attrs.comparisonValues = generateComparisonValues(
          this.trends,
          trendOptionsMap.get(benchmark),
        );
      }

      if (benchmark == "vs_org") {
        attrs.comparisonValues = generateOrgComparisonValues(
          this.trends,
          Math.round(this.selectedKpi?.org_score),
        );
      }

      return new LineChart(attrs);
    },
    renderDriverDistributionChart() {
      if (!this.hasMultipleTrendValues) return;
      const elChart = document.getElementById(`kpi-distribution-chart`);
      if (!elChart) return;

      elChart.innerHTML = null;
      const benchmark = this.compareTo;
      const minVal = Math.min(
        ...this.trends.values
          .map((v) => v.value)
          .concat(this.trends.factor.vs_50th),
      );
      const maxVal = Math.max(
        ...this.trends.values
          .map((v) => v.value)
          .concat(this.trends.factor.vs_90th),
      );

      const trendSize = this.trends.values.length;
      this.xEnd = this.trends.values[trendSize - 1]?.label;
      // Avoid using 'this' in passing in function for the tooltip callback, get the string explicitly

      // Filter out null values before passing to chart
      const validValues = this.trends.values.filter(
        (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
      );

      let stackedAreaValues = { series: [], dates: [] };
      let options = JSON.parse(this.selectedKpi?.options);

      // NOTE: 'Ease of delivery', 'Engagement', 'Perceived Productivity' direction reversing
      if (
        this.selectedKpi?.engagement ||
        this.selectedKpi?.effort_to_deliver ||
        this.selectedKpi?.id == 328
      )
        options = options.reverse();

      const colors = getDistributionColors(options.length);

      options.forEach((opt, idx) => {
        stackedAreaValues.series.push({
          name: opt,
          color: colors[idx],
          counts: [],
          key: `${options.length < 6 ? numberToSpelling(idx + 1) : numberToSpelling(idx)}_count`,
          // NOTE: Differing key access for options w/ 6 values
        });
      });

      // Formatting the trend data to match the required format the stacked area chart needs
      validValues.forEach((val) => {
        stackedAreaValues.dates.push(val.date);

        stackedAreaValues.series.forEach((breakdownValue) => {
          breakdownValue.counts.push(val.breakdown[breakdownValue.key]);
        });
      });

      // Reverse so that the stacks match the order of the distribution chart
      stackedAreaValues.series = stackedAreaValues.series.reverse();

      const attrs = {
        elChart: elChart,
        startDate: this.xStart,
        endDate:
          validValues[validValues.length - 1]?.date ||
          this.trends.values[trendSize - 1]?.date,
        tooltipHtml(d, cd) {
          const params = {
            heading: new Intl.DateTimeFormat("en-US", {
              month: "short",
              year: "numeric",
              timeZone: "UTC",
            }).format(d),
            rows: cd
              .map((r) => ({
                label: r.name,
                value: r.count,
                formattedValue: Math.round(r.percentage * 100) + "%",
                color: r.color,
              }))
              .reverse(),
          };

          return chartTooltipHtml.default(params);
        },
        yAxisMin: minVal * 0.8,
        yAxisMax: maxVal * 1.2,
        showXAxisTicks: false,
        values: stackedAreaValues,
      };

      if (
        benchmark &&
        (this.trends.factor[benchmark] || benchmark === "vs_prev")
      ) {
        attrs.comparisonValues = generateComparisonValues(
          this.trends,
          benchmark,
        );
      }

      return new StackedAreaChart(attrs);
    },
    get breakdownItems() {
      return this.getBreakdownItems({
        sortBy: this.drilldownSortBy,
        compareTo: this.compareTo,
        reverse: this.drilldownSortReverse,
        orgScore: this.selectedKpi?.org_score,
      });
    },
    isDrilldownSortOrder(order) {
      return this.drilldownSortBy == order;
    },
    changeComparison(compareTo) {
      this.$store.snapshotResultFilters.setCompareTo(compareTo);
      if (this.compareTo == "vs_prev") this.fetchAll();
      // Update any trends comparison data if it's showing
      if (this.selectedDrilldownTab == "overview") {
        this.renderDriverTrendChart();
        this.renderDriverDistributionChart();
      }
    },
    changeSortBy(sortBy) {
      if (this.sortBy == sortBy) {
        this.$store.snapshotResultFilters.sortReverse = !this.sortReverse;

        setQueryStringParam("sr", this.sortReverse);
        return;
      }

      this.$store.snapshotResultFilters.sortBy = sortBy;
      this.$store.snapshotResultFilters.sortReverse = false;

      setQueryStringParam("sb", sortBy);
      setQueryStringParam("sr", false);
    },
    changeDrilldownSortBy(sortBy) {
      if (this.drilldownSortBy == sortBy) {
        this.$store.snapshotResultFilters.drilldownSortReverse =
          !this.drilldownSortReverse;

        setQueryStringParam("ddsr", this.drilldownSortReverse);
        return;
      }

      this.$store.snapshotResultFilters.drilldownSortBy = sortBy;
      this.$store.snapshotResultFilters.drilldownSortReverse = false;

      setQueryStringParam("ddsb", sortBy);
      setQueryStringParam("ddsr", false);
    },
    // Note: changeSquadIds and changeGroupIds methods are provided by peopleFiltersMixin

    changeTagIds(tagIds) {
      this.$store.snapshotResultFilters.tagIds = tagIds;
      setQueryStringParam("tag_ids", tagIds);

      // Org comparison should only be allowed to be set when there are filters applied
      if (
        this.squadIds.length == 0 &&
        this.tagIds.length == 0 &&
        this.compareTo == "vs_org"
      ) {
        setQueryStringParam("ct", null);
      }

      this.fetchAll();

      // Only clear tree state and refresh team breakdown if modal is actually open
      if (
        this.selectedDrilldownTab === "team_breakdown" &&
        this.drilldownModalOpen
      ) {
        this.resetTreeState();
        this.refetchTeamBreakdown();
      }
    },
    refetchTeamBreakdown() {
      this.resetTreeState();
      this.isFetchingTableData = true;

      const selectedItem = drilldownTabs.find(
        (t) => t.tab == this.selectedDrilldownTab,
      );

      if (selectedItem) {
        this.fetchTab(this.selectedDrilldownTab, selectedItem.url);
      }
    },
    handleAggregationChange(detail) {
      if (detail.methodName && typeof this[detail.methodName] === "function") {
        this[detail.methodName]();
      }
    },
    handleComparisonChange() {
      this.fetchAll();

      const currentTab = this.selectedDrilldownTab;
      const tabConfig = this.visibleDrilldownTabs.find(
        (t) => t.tab === currentTab,
      );

      if (currentTab && tabConfig?.url) {
        this.fetchTab(currentTab, tabConfig.url);
      }
    },

    // Get group names for tooltips using mixin resolver
    getGroupNames() {
      return this.resolveGroupNames(this.groups, this.getCurrentGroupIds());
    },
  };
}
